import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wallet as WalletIcon, Power, Pencil, BookOpen, Sparkles, ArrowLeft, Palette, PaintBucket, X } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { formatPhone } from "../utils/phone";
import LanguageSelector from "../components/LanguageSelector";
import AnimatedFeatureIcon from "../components/AnimatedFeatureIcon";

const FEATURE_BASE = [
  { type: "pencil",        bg: "bg-yellow-300", icon: Pencil,      titleKey: "pencil",        descKey: "pencil_desc",        priceKey: "pencil_price" },
  { type: "coloring",      bg: "bg-orange-300", icon: Palette,     titleKey: "coloring",      descKey: "coloring_desc",      priceKey: "coloring_price" },
  { type: "colour-guide",  bg: "bg-lime-300",   icon: PaintBucket, titleKey: "colour_guide",  descKey: "colour_guide_desc",  priceKey: "colour_guide_price" },
  { type: "story",         bg: "bg-pink-300",   icon: BookOpen,    titleKey: "story",         descKey: "story_desc",         priceKey: "story_price" },
];

// The color_type strings sent to the backend MUST match the whitelist in
// server.py (ALLOWED_COLOR_TYPES) — they're interpolated verbatim into the
// Gemini prompt. Display labels are translated via i18n; raw values are not.
const COLOR_STYLES = [
  { value: "Oil Pastel",         labelKey: "coloring_style_oil_pastel",        bg: "bg-amber-200" },
  { value: "Watercolor",         labelKey: "coloring_style_watercolor",        bg: "bg-sky-200" },
  { value: "Graphite Shade",     labelKey: "coloring_style_graphite_shade",    bg: "bg-slate-200" },
  { value: "Color Pencil Shade", labelKey: "coloring_style_color_pencil_shade", bg: "bg-emerald-200" },
  { value: "Oil Paint",          labelKey: "coloring_style_oil_paint",         bg: "bg-rose-200" },
];

export default function Drawing() {
  const { user, lang, config, logout } = useAuth();
  const navigate = useNavigate();
  const balance = user?.wallet_balance ?? 0;
  const currency = config?.currency_symbol || "₹";
  const features = FEATURE_BASE.map((f) => ({ ...f, cost: config?.[f.priceKey] ?? 0 }));
  const [pickingStyle, setPickingStyle] = useState(false);

  const onFeatureClick = (type) => {
    if (type === "coloring") {
      setPickingStyle(true);
      return;
    }
    navigate(`/drawing/capture/${type}`);
  };

  const onStylePick = (styleValue) => {
    setPickingStyle(false);
    // Persist the chosen color_type via sessionStorage so a refresh on the
    // capture page still knows the style. The Capture page reads it back.
    try { sessionStorage.setItem("dt_coloring_style", styleValue); } catch (_e) { /* ignore */ }
    navigate(`/drawing/capture/coloring?color_type=${encodeURIComponent(styleValue)}`);
  };

  return (
    <div className="page-shell" data-testid="drawing-page">
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
          aria-label="back to home"
        >
          <ArrowLeft size={18} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <button
          data-testid="wallet-pill"
          onClick={() => navigate("/wallet")}
          className="flex items-center gap-2 bg-emerald-400 hover:bg-emerald-500 text-white px-4 py-2 rounded-full font-bold shadow-[0_4px_0_rgba(0,0,0,0.1)] border-2 border-emerald-600/20 active:translate-y-1 active:shadow-none transition-all"
        >
          <WalletIcon size={18} strokeWidth={2.5} />
          {currency}<span data-testid="wallet-balance">{balance}</span>
        </button>
        <div className="flex items-center gap-2">
          <LanguageSelector compact />
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
            aria-label="logout"
          >
            <Power size={18} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1 rounded-full border-2 border-slate-900/10 text-sm font-bold text-slate-600">
          <Sparkles size={16} strokeWidth={2.5} className="text-pink-500" /> {t(lang, "welcome")}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "app_name")}
        </h1>
        <p className="text-base sm:text-lg text-cyan-50/95 mt-1 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">{t(lang, "tagline")}</p>
      </motion.div>

      <div className="mt-8 flex flex-col gap-5">
        {features.map((f, i) => {
          return (
            <motion.button
              key={f.type}
              data-testid={`feature-${f.type}-btn`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * i }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onFeatureClick(f.type)}
              className={`relative w-full ${f.bg} rounded-3xl p-5 text-left card-squish border-4 border-white flex items-center gap-4`}
            >
              <div className="w-16 h-16 rounded-2xl bg-white/90 flex items-center justify-center shadow-inner">
                <AnimatedFeatureIcon type={f.type} icon={f.icon} size={32} className="text-slate-800" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-2xl font-bold text-slate-800 truncate">
                  {t(lang, f.titleKey)}
                </h3>
                <p className="text-sm text-slate-700 font-semibold mt-0.5 truncate">
                  {t(lang, f.descKey)}
                </p>
              </div>
              <div className={`shrink-0 px-3 py-1.5 rounded-full font-heading font-bold text-base ${
                f.cost === 0 ? "bg-emerald-500 text-white" : "bg-white text-slate-800 border-2 border-slate-900/10"
              }`}>
                {f.cost === 0 ? t(lang, "free_label") : `${currency}${f.cost}`}
              </div>
            </motion.button>
          );
        })}
      </div>

      <p className="mt-8 text-center text-xs text-cyan-100/80 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">
        {formatPhone(user?.phone)}
      </p>

      <AnimatePresence>
        {pickingStyle && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setPickingStyle(false)}
            data-testid="coloring-style-picker"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 28 }}
              className="w-full max-w-md bg-white rounded-3xl p-5 border-4 border-white shadow-[0_10px_0_rgba(0,0,0,0.15)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-xl font-bold text-slate-800">
                  {t(lang, "coloring_choose_style")}
                </h3>
                <button
                  data-testid="coloring-style-close"
                  onClick={() => setPickingStyle(false)}
                  className="p-2 rounded-full bg-slate-100 hover:bg-slate-200 btn-squish"
                  aria-label="close"
                >
                  <X size={18} strokeWidth={2.5} className="text-slate-600" />
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2.5">
                {COLOR_STYLES.map((s) => (
                  <button
                    key={s.value}
                    data-testid={`coloring-style-${s.value.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => onStylePick(s.value)}
                    className={`${s.bg} rounded-2xl px-4 py-3 border-2 border-white shadow-[0_3px_0_rgba(0,0,0,0.08)] btn-squish flex items-center gap-3 text-left`}
                  >
                    <Palette size={20} strokeWidth={2.5} className="text-slate-700 shrink-0" />
                    <span className="font-heading text-base font-bold text-slate-800">
                      {t(lang, s.labelKey)}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
